import React, { useEffect, useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, Loader2, RotateCcw, Printer, RefreshCw } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { fmtMoney } from "../money";
import GatewayWallet from "@/gateway/components/GatewayWallet";
import { PAYOUT_SETUP_ANCHOR } from "./payoutSetupAnchor";


// QA2 H2-10 — the practitioner's revenue number was the GROSS one, everywhere, forever:
// the API already sends `fee_breakdown: {commission_cents, net_to_practitioner_cents,
// fee_pct}` per row and the table threw it away. `application_fee_cents` is the older,
// flatter form of the same fact (rail-B destination charges) and is the fallback; a row
// carrying neither returns null, and null renders as "—" rather than as a guessed zero —
// "no commission" and "commission unknown" are not the same claim.
const feeOf = (tx) => {
  const fb = tx.fee_breakdown;
  if (fb && Number.isFinite(fb.commission_cents)) {
    return {
      commission: fb.commission_cents,
      net: Number.isFinite(fb.net_to_practitioner_cents)
        ? fb.net_to_practitioner_cents
        : (tx.amount_cents || 0) - fb.commission_cents,
      pct: fb.fee_pct,
    };
  }
  if (Number.isFinite(tx.application_fee_cents)) {
    return {
      commission: tx.application_fee_cents,
      net: (tx.amount_cents || 0) - tx.application_fee_cents,
      pct: null,
    };
  }
  return null;
};

/** Cents that have already gone back on this sale. */
const refundedOf = (tx) => Number(tx.refunded_cents || 0);

/** The refundable balance — the same subtraction `service.refund_transaction_for_tenant`
 *  makes before it reserves, so the number offered here is the number the server allows. */
const remainingOf = (tx) => Math.max(0, Number(tx.amount_cents || 0) - refundedOf(tx));

/**
 * How much of this sale has come back — `"none"` | `"some"` | `"full"`, and it reads BOTH
 * witnesses because either one alone can be behind.
 *
 * `refunded_cents` is the AMOUNT (a stamped mirror of Stripe's own
 * `charge.amount_refunded`) and `refund_status` is the settlement's own word for it. They
 * move together on an in-product refund, but they are written by four different paths —
 * the legacy accumulator, the gateway stamp, the `charge.refunded` mirror and the
 * unverified fence — so "anything came back" is their DISJUNCTION, and only "both say
 * nothing came back" is an untouched sale. In particular `refund_status: "unverified"`
 * (`service.refund_transaction_for_tenant`'s fence: the ledger committed and Stripe never
 * confirmed) is "some", never "none".
 */
const refundState = (tx) => {
  const refunded = refundedOf(tx);
  const status = tx.refund_status || "none";
  if (refunded <= 0 && status === "none") return "none";
  if (refunded >= Number(tx.amount_cents || 0) || status === "refunded") return "full";
  return "some";
};

/**
 * Does this row contribute to the totals under the table?
 *
 * ONE definition, read by the totals AND by the colour of the two money cells, because a
 * row painted as earnings that the totals below it exclude is precisely the contradiction
 * BUG-016's re-open comment describes.
 */
const countsToward = (tx) => tx.payment_status === "paid" && refundState(tx) === "none";

/**
 * WHAT THE PRACTITIONER KEPT on this sale, and what Solunary kept with it — which is not
 * the capture-time snapshot the moment any money has gone back (BUG-061, BUG-016).
 *
 * Three answers, and the difference between the last two is the whole point:
 *
 *   {commission, net, pct}   nothing was refunded → `feeOf`'s snapshot, unchanged.
 *   {commission: 0, net: 0}  the sale came back IN FULL → zero, and this is EXACT rather
 *                            than a convenient rounding: `gateway/refunds.allocate`
 *                            detects `amount == rem_gross` and returns the entire
 *                            remaining commission and the entire remaining payable, so a
 *                            fully refunded sale leaves the practice and the platform
 *                            with nothing on it.
 *   null                     the sale came back IN PART → UNKNOWN, deliberately. The
 *                            retained split of a partial refund is decided by the LEDGER,
 *                            pro-rated against ITS OWN remainders and explicitly never
 *                            against the original rate (`refunds.allocate`'s docstring),
 *                            and this row does not carry that number. Re-deriving it here
 *                            from `fee_breakdown × refunded_cents` would be a second
 *                            authority on a money figure, and it diverges from the ledger
 *                            by a cent the first time one sale is refunded twice.
 *
 * `null` renders "—", exactly as a row with no snapshot at all does, and it means the
 * same thing here as it does there: "commission unknown" is not "no commission".
 */
const earningsOf = (tx) => {
  const fee = feeOf(tx);
  if (!fee) return null;
  const state = refundState(tx);
  if (state === "none") return fee;
  if (state === "full") return { commission: 0, net: 0, pct: null };
  return null;
};

/**
 * A refund amount typed by a human → integer cents, or `null` when it is not an amount.
 *
 * STRING PARSING, NOT `Number(x) * 100`: this feeds a real refund, so "1.005" must be
 * REFUSED rather than silently floored by binary floating point, and `admin/money.toCents`
 * (`Math.round(Number(major || 0) * 100)`) answers 0 for "abc" and NaN-adjacent values for
 * anything else it does not understand. A comma is a decimal separator for the fr and es
 * locales this dashboard ships in, so it is accepted alongside the point.
 */
const parseAmountCents = (raw) => {
  const m = /^\s*(\d+)(?:[.,](\d{1,2}))?\s*$/.exec(String(raw ?? ""));
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] || "0").padEnd(2, "0"));
};

/** Integer cents → the string this dialog's amount field holds ("120.00"). */
const amountField = (cents) => (Math.max(0, Number(cents || 0)) / 100).toFixed(2);

/**
 * Sales the SERVER refuses to refund in part, so the dialog must not offer the field.
 *
 * Both refusals are in `service.refund_transaction_for_tenant` and both are about value
 * that is not ours to arithmetic on: a gift card's balance is bearer-held and may already
 * have been spent, and a split-tender purchase has no defensible answer to "which leg
 * does this partial come out of". Offering an amount box on these rows would put a
 * guaranteed 400 behind the submit button.
 */
const fullOnly = (tx) =>
  tx.kind === "giftcard" || Number((tx.metadata || {}).giftcard_applied_cents || 0) > 0;

/**
 * A row's classification for the filter chips AND the status cell — one rule, computed
 * once, so the chip a row is filed under and the badge it displays can never disagree.
 * A refund (partial or full) always outranks "paid": money that came back is the more
 * urgent fact about the row. Everything else that is not `payment_status: "paid"` is
 * "unpaid" — expired/initiated checkout sessions included, which is why the unpaid cell
 * renders the raw `status` value below rather than a translated label: the backend does
 * not promise that set is closed.
 */
const classify = (tx) => {
  if (refundState(tx) !== "none") return "refunded";
  if (tx.payment_status === "paid") return "paid";
  return "unpaid";
};

const CLASS_FILTERS = ["all", "paid", "unpaid", "refunded"];

/**
 * The Type filter's options — the seven checkout kinds, which is a CLOSED set: every
 * `payment_transactions.kind` is written by one of the seven flows priced through
 * `gateway/flows/orders.quote_hosted_order`, and this dictionary carries a label for each
 * in all three languages (`stx.kind.*`). Asking the server for the distinct kinds of a
 * practice would be one extra query per page load to narrow a list of seven — and
 * `.distinct` on a tenant-scoped collection is not tenant-scoped at all
 * (payments/repository.py's own module warning), so the honest cheap version is this one.
 */
const KIND_FILTERS = ["booking", "course", "giftcard", "invoice", "pathway", "product", "program",
                      "telehealth"];

/** One page a human reads. The route ceilings anything larger (repository.py). */
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;
const EMPTY_META = { total: 0, page: 1, page_size: PAGE_SIZE, totals: [] };

/**
 * StripeTransactions — liste des paiements Stripe encaissés sur le compte
 * PLATEFORME (gift cards, programmes, produits, factures), avec remboursement
 * (Stripe Refunds API via modules/payments).
 */

/**
 * Nonce de requête. `crypto.randomUUID` n'existe QUE dans un contexte sécurisé
 * (https / localhost) : sur une préprod en HTTP simple ou une IP de LAN, l'appel jette
 * AVANT le POST — l'admin voyait « remboursement échoué » sans qu'aucune requête ne
 * soit partie. Le champ est optionnel côté serveur : mieux vaut un nonce faible qu'un
 * bouton mort.
 */
const requestId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* contexte non sécurisé */ }
  return `rid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};
export const StripeTransactions = () => {
  const t = useT("admin");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [resending, setResending] = useState(null);
  // "all" by default: a practitioner opening this table for the first time after a
  // failed/abandoned checkout needs to SEE that row, not have it silently dropped the
  // way the old `payment_status === "paid"` filter dropped every unpaid one.
  const [filter, setFilter] = useState("all");
  // BUG-059 — the query is now the SERVER's: one page, plus the size of the set it came
  // from and the earnings figure over it. `meta` is that envelope; `items` is its rows.
  const [meta, setMeta] = useState(EMPTY_META);
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // TWO search states on purpose: `search` is what the practitioner has typed and
  // `query` is what has been SENT. The search is a Mongo regex scan, so firing it per
  // keystroke turns one typed address into a dozen collection scans.
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  // BUG-060 — the refund is a DIALOG, not a `window.confirm`, because the amount is an
  // input. `refundTarget` is the row awaiting an amount; `refundAmount` is what the
  // practitioner has typed, in MAJOR units, because that is what the field shows them.
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");

  useEffect(() => {
    const id = setTimeout(() => { setQuery(search.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/transactions", {
        params: {
          page, page_size: PAGE_SIZE,
          // Absent rather than empty: the route validates each parameter, and sending
          // `state=""` to an enum-checked field is a 422 for a filter nobody set.
          ...(query ? { q: query } : {}),
          ...(kind ? { kind } : {}),
          ...(filter !== "all" ? { state: filter } : {}),
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
        },
      });
      setItems(data?.rows || []);
      setMeta({
        total: Number(data?.total || 0),
        page: Number(data?.page || 1),
        page_size: Number(data?.page_size || PAGE_SIZE),
        totals: Array.isArray(data?.totals) ? data.totals : [],
      });
    } catch {
      setItems([]);
      setMeta(EMPTY_META);
    }
  }, [page, query, kind, filter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  /**
   * Every control that changes WHICH payments are being asked for goes through here.
   * Staying on page 9 of a set that has just been narrowed to 12 rows renders an empty
   * table, and an empty table reads as "you have no payments" rather than "you are past
   * the end of this filter".
   */
  const applyFilter = (setter) => (value) => { setter(value); setPage(1); };

  const openRefund = (tx) => {
    setRefundTarget(tx);
    // Pre-filled with the whole refundable balance: the common case is still "give it all
    // back", and it stays one extra click away from being exactly what it was before.
    setRefundAmount(amountField(remainingOf(tx)));
  };

  /**
   * The amount this dialog would send, as `{cents, full, error}`.
   *
   * `full` is what decides whether `amount_cents` travels at all — see `submitRefund`.
   * `error` is a translation KEY rather than a message so the caller renders it through
   * `t` like everything else on this screen.
   */
  const refundEntry = (tx) => {
    if (!tx) return { cents: null, full: false, error: null };
    const remaining = remainingOf(tx);
    if (fullOnly(tx)) return { cents: remaining, full: true, error: null };
    const cents = parseAmountCents(refundAmount);
    if (cents === null) return { cents: null, full: false, error: "stx.refundAmountInvalid" };
    if (cents <= 0) return { cents, full: false, error: "stx.refundAmountInvalid" };
    if (cents > remaining) return { cents, full: false, error: "stx.refundAmountTooHigh" };
    return { cents, full: cents >= remaining, error: null };
  };

  const submitRefund = async () => {
    const tx = refundTarget;
    const { cents, full, error } = refundEntry(tx);
    if (!tx || error) return;
    setBusy(tx.session_id);
    try {
      await api.post(`/admin/transactions/${tx.session_id}/refund`, {
        // `null` MEANS "the whole remaining balance", and it is not the same request as
        // naming that same number: the server then derives the amount from the row IT
        // holds (so a table that went stale cannot ask for more than remains) and takes
        // the unambiguous `refund:{sid}:full` Stripe idempotency key. A smaller amount is
        // named explicitly, and `client_request_id` is then what keeps that key
        // unambiguous between two partials of equal size — `refund_transaction_for_tenant`
        // refuses a partial without one.
        amount_cents: full ? null : cents,
        reason: "Admin refund from dashboard",
        client_request_id: requestId(),
      });
      toast.success(full ? t("stx.success") : t("stx.successPartial", {
        amount: fmtMoney(cents, tx.currency),
      }));
      setRefundTarget(null);
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("stx.error")));
    } finally {
      setBusy(null);
    }
  };

  const resendReceipt = async (tx) => {
    setResending(tx.session_id);
    try {
      await api.post(`/admin/receipts/${tx.receipt.receipt_number}/resend`);
      toast.success(t("stx.receiptResent", { email: tx.email }));
    } catch (e) {
      toast.error(extractApiError(e, t("stx.receiptResendError")));
    } finally {
      setResending(null);
    }
  };

  if (!items) return null;

  // The rows on screen ARE the page the server returned — the filter, the search and the
  // date range are its `$match`, not a `.filter()` over a truncated list (BUG-059).
  const visibleItems = items;
  // Totals come from the server for the same reason: they are about every payment the
  // filter matched, and the browser holds twenty-five of them. Per CURRENCY, never summed
  // across them. See `repository.admin_transaction_totals` for the basis.
  const totals = meta.totals;
  const searching = !!(query || kind || dateFrom || dateTo) || filter !== "all";
  const pageCount = Math.max(1, Math.ceil(meta.total / (meta.page_size || PAGE_SIZE)));
  const firstOnPage = meta.total === 0 ? 0 : (meta.page - 1) * meta.page_size + 1;
  const lastOnPage = Math.min(meta.total, (meta.page - 1) * meta.page_size + items.length);

  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6" data-testid="stripe-transactions">
      <h3 className="font-display text-xl flex items-center gap-2 mb-1">
        <CreditCard className="w-5 h-5 text-sol-gold" /> {t("stx.title")}
      </h3>
      <p className="text-sm text-eh-muted mb-4">
        {t("stx.desc")}
      </p>

      <div className="mb-4">
        {/* BUG-222 — the wallet refuses a withdrawal whose payout destination is not
            ready, and this is where it sends the practitioner to fix that: the Connect
            card higher up THIS page (`PaymentsAdmin` renders both). The id comes from
            the card's own module, so the link cannot outlive its target. */}
        <GatewayWallet payoutSetupHref={`#${PAYOUT_SETUP_ANCHOR}`} />
      </div>

      {/* BUG-059 — search, type, date range and the state chips. Every one of them is a
          SERVER parameter: filtering in the browser can only ever narrow the page it was
          handed, which is exactly how the 501st transaction became unreachable. */}
      <div className="mb-4 space-y-3" data-testid="stx-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-eh-muted block mb-1" htmlFor="stx-search">
              {t("stx.searchLabel")}
            </label>
            <Input
              id="stx-search"
              data-testid="stx-search"
              type="search"
              autoComplete="off"
              placeholder={t("stx.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-eh-muted block mb-1" htmlFor="stx-kind">
              {t("stx.colKind")}
            </label>
            <select
              id="stx-kind"
              data-testid="stx-kind"
              value={kind}
              onChange={(e) => applyFilter(setKind)(e.target.value)}
              className="h-9 rounded-md border border-eh-border-soft bg-white px-2 text-sm text-eh-ink"
            >
              <option value="">{t("stx.kindAny")}</option>
              {KIND_FILTERS.map((k) => (
                <option key={k} value={k}>{t(`stx.kind.${k}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-eh-muted block mb-1" htmlFor="stx-date-from">
              {t("stx.dateFrom")}
            </label>
            <Input
              id="stx-date-from"
              data-testid="stx-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => applyFilter(setDateFrom)(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-eh-muted block mb-1" htmlFor="stx-date-to">
              {t("stx.dateTo")}
            </label>
            <Input
              id="stx-date-to"
              data-testid="stx-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => applyFilter(setDateTo)(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {CLASS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => applyFilter(setFilter)(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                filter === f
                  ? "bg-eh-ink text-white border-eh-ink"
                  : "bg-white text-eh-muted border-eh-border-soft hover:bg-eh-cream"
              }`}
              data-testid={`stx-filter-${f}`}
            >
              {t(`stx.filter.${f}`)}
            </button>
          ))}
          {/* THE COUNT. Its absence was half of BUG-059: 500 rows on screen said nothing
              about whether there were 500 or 5000, so nothing told the practitioner that
              older payments existed at all. */}
          <span className="ml-auto text-xs text-eh-muted" data-testid="stx-count">
            {meta.total === 0
              ? t("stx.countNone")
              : t("stx.count", { from: firstOnPage, to: lastOnPage, total: meta.total })}
          </span>
        </div>
      </div>

      {visibleItems.length === 0 ? (
        searching ? (
          <div className="text-sm text-eh-muted py-4 text-center" data-testid="stripe-transactions-empty-filtered">
            {t("stx.emptyFiltered")}
          </div>
        ) : (
          <div className="text-sm text-eh-muted py-4 text-center" data-testid="stripe-transactions-empty">
            {t("stx.empty")}
          </div>
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-eh-muted border-b border-eh-border-soft">
                <th className="py-2 pr-4 font-medium">{t("stx.colKind")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colEmail")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colAmount")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colCommission", null, "Commission")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colNet", null, "Net to you")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colDate")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colStatus")}</th>
                <th className="py-2 pr-4 font-medium">{t("stx.colReceipt")}</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-eh-border-soft">
              {visibleItems.map((tx) => {
                const cls = classify(tx);
                // What was KEPT, never the capture-time snapshot on a row whose money has
                // gone back (BUG-061/016) — `earningsOf` states which of the three answers
                // this row gets and why.
                const fee = earningsOf(tx);
                const refundedCents = refundedOf(tx);
                const partlyBack = refundState(tx) === "some";
                const counts = countsToward(tx);
                const owed = tx.refund_obligation?.state === "owed" ? tx.refund_obligation : null;
                // Gate stays explicit on `payment_status`, not on the refund classification:
                // an UNPAID checkout has no charge to refund and no receipt to resend, a
                // PARTIALLY refunded one still does (there is remaining amount to return).
                const isPaid = tx.payment_status === "paid";
                const canRefund = isPaid && tx.refund_status !== "refunded";
                return (
                  <tr key={tx.session_id} data-testid={`st-row-${tx.session_id}`}>
                    <td className="py-3 pr-4 capitalize text-eh-ink">{tx.kind}</td>
                    <td className="py-3 pr-4 text-eh-muted truncate max-w-[200px]">{tx.email}</td>
                    <td className="py-3 pr-4 font-medium whitespace-nowrap">
                      <div>{fmtMoney(tx.amount_cents, tx.currency)}</div>
                      {refundedCents > 0 && (
                        <div
                          className={`mt-1 inline-flex text-[11px] font-normal px-1.5 py-0.5 rounded ${
                            tx.refund_status === "refunded" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                          }`}
                          data-testid={`st-refunded-${tx.session_id}`}
                        >
                          {tx.refund_status === "refunded"
                            ? t("stx.refundedFull", { amount: fmtMoney(refundedCents, tx.currency) })
                            : t("stx.refundedPartial", { amount: fmtMoney(refundedCents, tx.currency) })}
                        </div>
                      )}
                      {owed && (
                        <div
                          className="mt-1 inline-flex text-[11px] font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"
                          data-testid={`st-refund-owed-${tx.session_id}`}
                        >
                          {t("stx.refundOwed", { amount: fmtMoney(owed.owed_cents, tx.currency) })}
                        </div>
                      )}
                    </td>
                    {/* ONE COLOUR RULE, and it is the totals' own basis: rose and green
                        are this table's words for "charged" and "earned", so they appear
                        on exactly the rows the totals below count — paid, nothing back.
                        An unpaid checkout has earned nothing YET and a refunded sale has
                        earned nothing AT ALL; both read muted, which is what stops a row
                        contradicting the sentence under the table (BUG-061, BUG-016).
                        The `title` is what the practitioner reads when a partial refund
                        leaves the retained split unstated — see `earningsOf`. */}
                    <td
                      className={`py-3 pr-4 whitespace-nowrap ${counts ? "text-rose-600" : "text-eh-muted"}`}
                      title={partlyBack ? t("stx.retainedUnknown") : undefined}
                      data-testid={`st-commission-${tx.session_id}`}
                    >
                      {/* No "−0.00": a sign on a zero states a direction that is not there. */}
                      {fee ? `${fee.commission ? "−" : ""}${fmtMoney(fee.commission, tx.currency)}` : "—"}
                      {fee && fee.pct != null && <span className="text-eh-muted"> ({fee.pct}%)</span>}
                    </td>
                    <td
                      className={`py-3 pr-4 font-medium whitespace-nowrap ${counts ? "text-emerald-700" : "text-eh-muted"}`}
                      title={partlyBack ? t("stx.retainedUnknown") : undefined}
                      data-testid={`st-net-${tx.session_id}`}
                    >
                      {fee ? fmtMoney(fee.net, tx.currency) : "—"}
                    </td>
                    <td className="py-3 pr-4 text-eh-muted whitespace-nowrap">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 pr-4">
                      {cls === "refunded" ? (
                        <span className="text-xs bg-rose-100 text-rose-700 px-2.5 py-0.5 rounded-full">
                          {tx.refund_status === "partial" ? t("stx.status.partial") : t("stx.refunded")}
                        </span>
                      ) : cls === "paid" ? (
                        <span className="text-xs bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full">
                          {t("stx.status.paid")}
                        </span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-eh-ink px-2.5 py-0.5 rounded-full" data-testid={`st-status-raw-${tx.session_id}`}>
                          {tx.status}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      {isPaid && tx.receipt ? (
                        <div className="flex items-center gap-1">
                          <a
                            href={tx.receipt.view_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t("stx.viewReceipt")}
                            data-testid={`st-receipt-${tx.session_id}`}
                            className="p-1.5 hover:bg-eh-cream rounded text-eh-ink"
                          >
                            <Printer className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => resendReceipt(tx)}
                            disabled={resending === tx.session_id}
                            title={t("stx.resendReceipt")}
                            data-testid={`st-receipt-resend-${tx.session_id}`}
                            className="p-1.5 hover:bg-eh-cream rounded text-eh-ink disabled:opacity-50"
                          >
                            {resending === tx.session_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <span data-testid={`st-receipt-${tx.session_id}`}>—</span>
                      )}
                    </td>
                    <td className="py-3">
                      {canRefund && (
                        <Button size="sm" variant="outline" onClick={() => openRefund(tx)} disabled={busy === tx.session_id} data-testid={`refund-tx-${tx.session_id}`}>
                          {busy === tx.session_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                          {t("stx.refund")}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3" data-testid="stx-pager">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={meta.page <= 1}
            data-testid="stx-page-prev"
          >
            {t("stx.pagePrev")}
          </Button>
          <span className="text-xs text-eh-muted" data-testid="stx-page-of">
            {t("stx.pageOf", { page: meta.page, pages: pageCount })}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={meta.page >= pageCount}
            data-testid="stx-page-next"
          >
            {t("stx.pageNext")}
          </Button>
        </div>
      )}

      {totals.length > 0 && (
        <div className="mt-4 pt-4 border-t border-eh-border-soft space-y-1" data-testid="stx-totals">
          {totals.map((tot) => (
            <div key={tot.currency} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm" data-testid={`stx-total-${tot.currency}`}>
              <span className="text-eh-muted">{t("stx.totalGross", null, "Gross")} {fmtMoney(tot.gross, tot.currency)}</span>
              <span className="text-rose-600">{t("stx.totalCommission", null, "Solunary commission")} −{fmtMoney(tot.commission, tot.currency)}</span>
              <span className="font-medium text-emerald-700" data-testid={`stx-total-net-${tot.currency}`}>
                {t("stx.totalNet", null, "Net to you")} {fmtMoney(tot.net, tot.currency)}
              </span>
            </div>
          ))}
          <p className="text-xs text-eh-muted">{t("stx.totalsNote")}</p>
        </div>
      )}

      {/* BUG-060 — the refund dialog. The amount is an INPUT, defaulted to the whole
          refundable balance, ceilinged by it, and stated next to what has already gone
          back so the practitioner is never doing that subtraction in their head. */}
      <Dialog open={!!refundTarget} onOpenChange={(open) => { if (!open) setRefundTarget(null); }}>
        <DialogContent data-testid="stx-refund-dialog">
          <DialogHeader>
            <DialogTitle>{t("stx.refundTitle")}</DialogTitle>
            <DialogDescription data-testid="stx-refund-summary">
              {refundTarget && (
                <>
                  <span className="block">
                    {t("stx.refundCharged", {
                      amount: fmtMoney(refundTarget.amount_cents, refundTarget.currency),
                    })}
                  </span>
                  <span className="block">
                    {t("stx.refundAlready", {
                      amount: fmtMoney(refundedOf(refundTarget), refundTarget.currency),
                    })}
                  </span>
                  <span className="block font-medium text-eh-ink">
                    {t("stx.refundRemaining", {
                      amount: fmtMoney(remainingOf(refundTarget), refundTarget.currency),
                    })}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {refundTarget && (fullOnly(refundTarget) ? (
            <p className="text-sm text-eh-muted" data-testid="stx-refund-fullonly">
              {refundTarget.kind === "giftcard"
                ? t("stx.refundGiftcardOnly")
                : t("stx.refundSplitTenderOnly")}
            </p>
          ) : (
            <div className="space-y-1">
              <label className="text-sm text-eh-ink block" htmlFor="stx-refund-amount">
                {t("stx.refundAmountLabel", {
                  currency: (refundTarget.currency || "").toUpperCase(),
                })}
              </label>
              <Input
                id="stx-refund-amount"
                data-testid="stx-refund-amount"
                inputMode="decimal"
                autoComplete="off"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
              {refundEntry(refundTarget).error && (
                <p className="text-xs text-rose-600" data-testid="stx-refund-error">
                  {t(refundEntry(refundTarget).error, {
                    amount: fmtMoney(remainingOf(refundTarget), refundTarget.currency),
                  })}
                </p>
              )}
            </div>
          ))}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)} data-testid="stx-refund-cancel">
              {t("stx.refundCancel")}
            </Button>
            <Button
              onClick={submitRefund}
              disabled={!refundTarget || !!refundEntry(refundTarget).error
                || busy === refundTarget?.session_id}
              data-testid="stx-refund-submit"
            >
              {busy === refundTarget?.session_id && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {t("stx.refundConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StripeTransactions;
